import useSWR from "swr";
import { API_BASE_URL, DEV_EPISODE_LIMIT, isDev } from "@/lib/env";
import type { APIEpisode } from "@/api/client";

type EpisodeTags = {
  Theme?: string[];
  Topic?: string[];
  Format?: string[];
};

export interface Episode {
  guid: string;
  title: string;
  description: string;
  cleanedDescription: string;
  link: string;
  publishedDate: string;
  duration: string;
  audioUrl: string;
  episodeNumber: number | null;
  formatTags: string[];
  themeTags: string[];
  topicTags: string[];
  cleaningStatus: string;
  cleaningTimestamp: string;
  createdAt: string;
  updatedAt: string;
  collectionName?: string;
  podcast?: string;
  podcastMetadata?: {
    prettifiedName: string;
    emoji: string;
  };
  isIndexed?: boolean;
}

const parseTags = (tags: APIEpisode["tags"]): EpisodeTags => {
  if (!tags) return {};

  if (typeof tags === "string") {
    try {
      return JSON.parse(tags) as EpisodeTags;
    } catch {
      return {};
    }
  }

  return tags as EpisodeTags;
};

const convertEpisode = (apiEpisode: APIEpisode): Episode => {
  const parsedTags = parseTags(apiEpisode.tags);

  return {
    guid: apiEpisode.id,
    title: apiEpisode.title,
    description: apiEpisode.description ?? "",
    cleanedDescription: apiEpisode.description ?? "",
    link: "",
    publishedDate: apiEpisode.published_date ?? new Date().toISOString(),
    duration: apiEpisode.duration?.toString() ?? "",
    audioUrl: apiEpisode.audio_url ?? "",
    episodeNumber: apiEpisode.episode_number,
    formatTags: parsedTags.Format ?? [],
    themeTags: parsedTags.Theme ?? [],
    topicTags: parsedTags.Topic ?? [],
    cleaningStatus: "completed",
    cleaningTimestamp: "",
    createdAt: apiEpisode.published_date ?? "",
    updatedAt: apiEpisode.published_date ?? "",
    collectionName: apiEpisode.podcast_slug,
    podcast: apiEpisode.podcast_slug,
    podcastMetadata: apiEpisode.podcast_display_name
      ? {
          prettifiedName: apiEpisode.podcast_display_name,
          emoji: "🎧",
        }
      : undefined,
    isIndexed: apiEpisode.is_indexed,
  };
};

export function useEpisodes(podcastSlug: string | null | undefined) {
  const { data, error, isLoading, mutate } = useSWR<APIEpisode[]>(
    podcastSlug ? `${API_BASE_URL}/me/episodes?podcast_slug=${encodeURIComponent(podcastSlug)}` : null,
  );

  // Convert, limit in dev, and sort by date
  let episodes: Episode[] = (data ?? []).map(convertEpisode);

  if (isDev && typeof DEV_EPISODE_LIMIT === "number" && episodes.length > DEV_EPISODE_LIMIT) {
    episodes = episodes.slice(0, DEV_EPISODE_LIMIT);
  }

  episodes.sort((a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime());

  return {
    episodes,
    isLoading,
    isError: !!error,
    refreshEpisodes: mutate,
  };
}

// Helper to extract unique sorted tags from episodes
const uniqueSorted = (values: string[]) => [...new Set(values)].sort((a, b) => a.localeCompare(b));

export function useEpisodeTags(podcastSlug: string | null | undefined, taxonomy?: { Format?: string[]; Theme?: string[]; Topic?: string[] } | null) {
  const { episodes, isLoading } = useEpisodes(podcastSlug);

  // If taxonomy exists, use it directly; otherwise extract from episodes
  const formatTags = taxonomy?.Format
    ? [...taxonomy.Format].sort((a, b) => a.localeCompare(b))
    : uniqueSorted(episodes.flatMap((e) => e.formatTags));

  const themeTags = taxonomy?.Theme
    ? [...taxonomy.Theme].sort((a, b) => a.localeCompare(b))
    : uniqueSorted(episodes.flatMap((e) => e.themeTags));

  const topicTags = taxonomy?.Topic
    ? [...taxonomy.Topic].sort((a, b) => a.localeCompare(b))
    : uniqueSorted(episodes.flatMap((e) => e.topicTags));

  return {
    formatTags,
    themeTags,
    topicTags,
    isLoading,
  };
}
